class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    if (facetForm) {
      facetForm.addEventListener(
        "input",
        function (event) {
          if (!event.target.matches(".filter")) {
            this.debouncedOnSubmit.call(this, event);
          }
        }.bind(this)
      );
    }

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    if (document.readyState == "complete") {
      FacetFiltersForm.updateProgressBar();
    } else {
      window.addEventListener("DOMContentLoaded", () =>
        FacetFiltersForm.updateProgressBar()
      );
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);

        FacetFiltersForm.updateProgressBar();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);

    FacetFiltersForm.updateProgressBar();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
    document.dispatchEvent(new CustomEvent("facetsUpdated"));
  }

  static createProgressBar() {
    const progressBarContainer = document.getElementById(
      "progress-bar-container"
    );
    if (!progressBarContainer) {
      console.error("Progress bar container not found");
      return null;
    }

    const progressBarOuter = document.createElement("div");
    progressBarOuter.id = "progress-bar";

    const progressBarInner = document.createElement("div");
    progressBarInner.id = "progress-bar-inner";
    progressBarOuter.appendChild(progressBarInner);
    progressBarContainer.innerHTML = "";
    progressBarContainer.appendChild(progressBarOuter);

    return { outer: progressBarOuter, inner: progressBarInner };
  }

  static getTotalCount() {
    const totalCountElement = document.getElementById("ProductCount");
    if (!totalCountElement) {
      console.error("Total count element not found");
      return null;
    }

    const totalCountMatch = totalCountElement.textContent.match(/\d+/);
    return parseInt(totalCountMatch ? totalCountMatch[0] : 0);
  }

  static updateProgressBar() {
    const progressBarElements = FacetFiltersForm.createProgressBar();
    if (!progressBarElements) return;
    const totalCount = FacetFiltersForm.getTotalCount();
    if (!totalCount && totalCount === null) return;

    const loadedProducts = document.querySelectorAll(
      "#productGrid li.grid__item"
    ).length;

    const progressElement = document.getElementById("progress");
    const totalElement = document.getElementById("total");

    if (progressElement) progressElement.innerHTML = `${loadedProducts}`;
    if (totalElement) totalElement.innerHTML = `${totalCount}`;

    const progressPercentage = Math.min(
      (loadedProducts / totalCount) * 100,
      100
    );
    const progressBarInner = document.getElementById("progress-bar-inner");
    if (!progressBarInner) return;
    progressBarInner.style.width = `${progressPercentage}%`;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    let url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener("DOMContentLoaded", () => {
  FacetFiltersForm.updateProgressBar();

  const productGrid = document.getElementById("productGrid");
  if (productGrid) {
    const observer = new MutationObserver(() => {
      FacetFiltersForm.updateProgressBar();
    });

    observer.observe(productGrid, {
      childList: true,
      subtree: true,
    });
  }

  /* document.addEventListener('scroll', function () {
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }); */
});

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minInput = this.querySelector(".range-slider__input--min");
    this.maxInput = this.querySelector(".range-slider__input--max");
    this.minLabel = this.querySelector(".range-slider__value--min");
    this.maxLabel = this.querySelector(".range-slider__value--max");
    this.rangeTrack = this.querySelector(".range-slider__range");

    this.updateLabels();
    this.updateTrack();

    this.minInput.addEventListener("input", this.onMinInputChange.bind(this));
    this.maxInput.addEventListener("input", this.onMaxInputChange.bind(this));
  }

  onMinInputChange() {
    if (parseFloat(this.minInput.value) > parseFloat(this.maxInput.value)) {
      this.minInput.value = this.maxInput.value;
    }

    this.updateLabels();
    this.updateTrack();
  }
  onMaxInputChange() {
    if (parseFloat(this.maxInput.value) < parseFloat(this.minInput.value)) {
      this.maxInput.value = this.minInput.value;
    }

    this.updateLabels();
    this.updateTrack();
  }

  updateLabels() {
    if (this.minLabel && this.maxLabel) {
      this.minLabel.textContent = this.minInput.value;
      this.maxLabel.textContent = this.maxInput.value;
    }
  }

  updateTrack() {
    const min = parseFloat(this.minInput.min);
    const max = parseFloat(this.maxInput.max);
    const minVal = parseFloat(this.minInput.value);
    const maxVal = parseFloat(this.maxInput.value);

    const percentMin = ((minVal - min) / (max - min)) * 100;
    const percentMax = ((maxVal - min) / (max - min)) * 100;

    if (this.rangeTrack) {
      this.rangeTrack.style.left = percentMin + "%";
      this.rangeTrack.style.width = percentMax - percentMin + "%";
    }
  }

  setMinAndMaxValues() {
    this.minInput.setAttribute("max", this.maxInput.value);
    this.maxInput.setAttribute("min", this.minInput.value);
  }

  adjustToValidValues() {
    const minValue = Number(this.minInput.value);
    const maxValue = Number(this.maxInput.value);

    if (minValue > maxValue) {
      if (event.target === this.minInput) {
        this.maxInput.value = minValue;
      } else {
        this.minInput.value = maxValue;
      }
    }
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class Ajaxinate {
  constructor(config) {
    this.settings = Object.assign(
      {
        pagination: ".AjaxinatePagination",
        method: "click",
        container: ".AjaxinateLoop",
        offset: 0,
        loadingText: "Loading...",
        callback: null,
      },
      config || {}
    );

    this.addScrollListeners = this.addScrollListeners.bind(this);
    this.addClickListener = this.addClickListener.bind(this);
    this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
    this.stopMultipleClicks = this.stopMultipleClicks.bind(this);
    this.destroy = this.destroy.bind(this);
    this.containerElement = document.querySelector(this.settings.container);
    this.paginationElement = document.querySelector(this.settings.pagination);
    if (this.containerElement && this.paginationElement) {
      this.initialize();
    }
  }
  initialize() {
    const initializers = {
      click: this.addClickListener,
      scroll: this.addScrollListeners,
    };
    initializers[this.settings.method]();
  }
  addScrollListeners() {
    if (this.paginationElement) {
      window.addEventListener("scroll", this.checkIfPaginationInView);
      window.addEventListener("resize", this.checkIfPaginationInView);
      window.addEventListener(
        "orientationchange",
        this.checkIfPaginationInView
      );
      this.checkIfPaginationInView();
    }
  }
  addClickListener() {
    if (this.paginationElement) {
      this.nextPageLinkElement = this.paginationElement.querySelector("a");
      this.nextPageLinkElement &&
        this.nextPageLinkElement.addEventListener(
          "click",
          this.stopMultipleClicks
        );
    }
  }
  stopMultipleClicks(event) {
    event.preventDefault();
    if (this.nextPageLinkElement.classList.contains("loading")) return;
    this.nextPageLinkElement.classList.add("loading");
    this.nextPageLinkElement.textContent = this.settings.loadingText;
    this.loadMore();
  }
  checkIfPaginationInView() {
    if (
      this.paginationElement.getBoundingClientRect().top - window.innerHeight <=
      this.settings.offset
    ) {
      this.loadMore();
      this.removeScrollListener();
    }
  }
  loadMore() {
    if (!this.paginationElement) return;
    const nextPageLink = this.paginationElement.querySelector("a");
    if (!nextPageLink) return;
    const nextPageUrl = nextPageLink.href;
    this.request = new XMLHttpRequest();
    this.request.onreadystatechange = () => {
      if (this.request.readyState === 4 && this.request.status === 200) {
        const responseDOM = new DOMParser().parseFromString(
          this.request.responseText,
          "text/html"
        );
        const newContainer = responseDOM.querySelector(this.settings.container);
        const newPagination = responseDOM.querySelector(
          this.settings.pagination
        );

        if (newContainer) {
          this.containerElement.insertAdjacentHTML(
            "beforeend",
            newContainer.innerHTML
          );
        }

        if (newPagination) {
          this.paginationElement.innerHTML = newPagination.innerHTML;
        } else {
          this.paginationElement.innerHTML = "";
          this.removeScrollListener();
        }
        if (
          this.settings.callback &&
          typeof this.settings.callback == "function"
        ) {
          this.settings.callback(responseDOM);
        }
        FacetFiltersForm.updateProgressBar();
        this.initialize();
      }
    };
    this.request.open("GET", nextPageUrl);
    this.request.send();
  }
  removeClickListener() {
    if (this.paginationElement) {
      const nextPageLink = this.paginationElement.querySelector("a");
      if (nextPageLink) {
        nextPageLink.removeEventListener("click", this.stopMultipleClicks);
      }
    }
  }
  removeScrollListener() {
    window.removeEventListener("scroll", this.checkIfPaginationInView);
    window.removeEventListener("resize", this.checkIfPaginationInView);
    window.removeEventListener(
      "orientationchange",
      this.checkIfPaginationInView
    );
  }
  destroy() {
    const destroyers = {
      click: this.removeClickListener,
      scroll: this.removeScrollListener,
    };
    destroyers[this.settings.method]();
    return this;
  }
}

let endlessScroll = null;

document.addEventListener("DOMContentLoaded", () => {
  initAjaxinate();

  document.addEventListener("facetsUpdated", function () {
    initAjaxinate();
  });
});

function initAjaxinate() {
  if (endlessScroll) {
    endlessScroll.destroy();
  }

  endlessScroll = new Ajaxinate({
    container: "#productGrid",
    pagination: "#collection-grid-pagination",
    method: "click",
    callback: function () {
      FacetFiltersForm.updateProgressBar();
    },
  });
}

function searchFacets() {
  let divWithSearch = document.querySelectorAll(
    ".mobile-facets__details:has(.search__input.field__input.filter)"
  );

  divWithSearch.forEach((div) => {
    let searchInput = div.querySelector(".search__input.field__input.filter");
    let listItems = div.querySelectorAll(".list-menu__item");

    searchInput.addEventListener("input", function () {
      let filter = searchInput.value.toLowerCase();

      listItems.forEach((item) => {
        let text = item
          .querySelector(".facet-checkbox__text")
          .textContent.toLowerCase();
        if (text.includes(filter)) {
          item.style.display = "";
        } else {
          item.style.display = "none";
        }
      });
    });
  });
}

document.addEventListener("DOMContentLoaded", function () {
  searchFacets();
});

const mobileFacetsClose = document.querySelector(
  ".mobile-facets__header .button-close"
);
if (mobileFacetsClose) {
  mobileFacetsClose.addEventListener("click", function () {
    document.body.classList.remove(`overflow-hidden`);
  });
}

function initializeColumnLayout(container) {
  const oneColumnButton = container.querySelector('[data-action="one-column"]');
  const twoColumnsButton = container.querySelector(
    '[data-action="two-columns"]'
  );
  const fourColumnsButton = container.querySelector(
    '[data-action="four-columns"]'
  );
  const gridColumnContainer = document.getElementById("productGrid");

  if (!gridColumnContainer) return;

  function updateGridColumnClass(columns) {
    if (columns === 4) {
      gridColumnContainer.classList.add("grid--4-col-desktop");
      gridColumnContainer.classList.remove("grid--2-col-desktop");
      gridColumnContainer.classList.remove("grid--6-col-desktop");
    } else if (columns === 2) {
      gridColumnContainer.classList.add("grid--2-col-desktop");
      gridColumnContainer.classList.remove("grid--4-col-desktop");
      gridColumnContainer.classList.remove("grid--6-col-desktop");
    } else if (columns === 6) {
      gridColumnContainer.classList.remove("grid--2-col-desktop");
      gridColumnContainer.classList.remove("grid--4-col-desktop");
      gridColumnContainer.classList.add("grid--6-col-desktop");
    }
  }

  function setActiveButton(button, isActive) {
    if (button) {
      button.classList.toggle("active", isActive);
    }
  }

  const currentColumns = parseInt(
    localStorage.getItem("columnPreference") || "4"
  );

  if (currentColumns === 2) {
    setActiveButton(oneColumnButton, true);
    setActiveButton(twoColumnsButton, false);
    setActiveButton(fourColumnsButton, false);
  } else if (currentColumns === 4) {
    setActiveButton(oneColumnButton, false);
    setActiveButton(twoColumnsButton, true);
    setActiveButton(fourColumnsButton, false);
  } else if (currentColumns === 6) {
    setActiveButton(oneColumnButton, false);
    setActiveButton(twoColumnsButton, false);
    setActiveButton(fourColumnsButton, true);
  }

  updateGridColumnClass(currentColumns);

  const cardContent = gridColumnContainer.querySelectorAll(".card__content");
  cardContent.forEach((content) => {
    content.style.display = currentColumns == 6 ? "none" : "grid";
  });

  if (oneColumnButton) {
    oneColumnButton.addEventListener("click", function (event) {
      event.preventDefault();
      gridColumnContainer
        .querySelectorAll(".card__content")
        .forEach((content) => {
          content.style.display = "grid";
        });
      updateGridColumnClass(2);
      setActiveButton(oneColumnButton, true);
      setActiveButton(twoColumnsButton, false);
      setActiveButton(fourColumnsButton, false);
      localStorage.setItem("columnPreference", "2");
    });
  }

  if (twoColumnsButton) {
    twoColumnsButton.addEventListener("click", function (event) {
      event.preventDefault();
      gridColumnContainer
        .querySelectorAll(".card__content")
        .forEach((content) => {
          content.style.display = "grid";
        });
      updateGridColumnClass(4);
      setActiveButton(oneColumnButton, false);
      setActiveButton(twoColumnsButton, true);
      setActiveButton(fourColumnsButton, false);
      localStorage.setItem("columnPreference", "4");
    });
  }

  if (fourColumnsButton) {
    fourColumnsButton.addEventListener("click", function (event) {
      event.preventDefault();
      gridColumnContainer
        .querySelectorAll(".card__content")
        .forEach((content) => {
          content.style.display = "none";
        });
      updateGridColumnClass(6);
      setActiveButton(oneColumnButton, false);
      setActiveButton(twoColumnsButton, false);
      setActiveButton(fourColumnsButton, true);
      localStorage.setItem("columnPreference", "6");
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  let container;
  if (window.matchMedia("(min-width: 990px)").matches) {
    container = document.getElementById("FacetSortForm");
  } else {
    container = document.querySelector(
      "#main-collection-filters-vertical .facets-container"
    );
  }
  if (!container) return;
  initializeColumnLayout(container);
  document.addEventListener("facetsUpdated", function () {
    initializeColumnLayout(container);
  });
});
